/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import * as textConstants from '../../constants/StudioTextConstants';
import utils from '../../../../common/utils';

export default class UpdateGraphAccessService extends BaseService {
  constructor() {
    super(constants.UPDATE_GRAPH_ACCESS, 'updateGraphAccessStatus');
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { graphId, email, accessType, operation } = payload;

    // operation can be 'update' (change access type) or 'revoke' (remove access)
    const requestBody = { email, accessType, operation };

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').put(`/graphs/${graphId}/access`, requestBody, {
        headers: enhancedHeaders
      });
    }
    return this.getClient('aiadmin').put(`/graphs/${graphId}/access`, requestBody, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    const graphAccessModel = Framework.getStoreRegistry().getStore('graphAccessModel');

    if (data && data.success) {
      // Pass raw response data to model for processing
      graphAccessModel.setUpdateSuccess(data);
    } else {
      graphAccessModel.setError(data?.message || textConstants.GRAPH_ACCESS_UPDATE_ERROR, 'update');
    }
  }

  handleError(error) {
    const graphAccessModel = Framework.getStoreRegistry().getStore('graphAccessModel');
    const { response } = error;

    if (response) {
      const { status, data } = response;
      if (status === 401) {
        utils.redirectToLogin();
      }
      graphAccessModel.setError(data?.message || textConstants.GRAPH_ACCESS_UPDATE_ERROR, 'update');
      const notification = {
        open: true,
        message: `${textConstants.GRAPH_ACCESS_UPDATE_ERROR}: ${data?.message || 'Unknown error'}`,
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
    } else if (error.message === 'timeout of 60000ms exceeded') {
      graphAccessModel.setError('Request timeout. Please try again.', 'update');
      const notification = {
        open: true,
        message: 'Request timeout. Please try again.',
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
    } else {
      graphAccessModel.setError(error.message || textConstants.GRAPH_ACCESS_UPDATE_ERROR, 'update');
    }
  }
}
